// src/components/dashboard/DashboardKegsTab.jsx
// ============================================================
// KEGS TAB - Dashboard keg listing and filtering
// ============================================================
// Displays filterable table of all kegs with status indicators.
// Props: filteredKegs, settings, onViewKeg, onOpenQRModal, onEditKeg, onDeleteKeg
// ============================================================

import React from 'react';

/**
 * StatusBadge - Renders a colored badge for keg status
 * @param {string} status - The status text to display
 * @param {string} type - 'tap' or 'clean' to determine color scheme
 */
const StatusBadge = ({ status, type }) => {
  const getStatusClasses = () => {
    if (type === 'tap') {
      return 'bg-green-100 text-green-800';
    }
    // Cleaning status colors
    switch (status) {
      case 'Dirty': return 'bg-red-100 text-red-800';
      case 'Rinsed': return 'bg-orange-100 text-orange-800';
      case 'Clean': return 'bg-yellow-100 text-yellow-800';
      case 'Sanitized': return 'bg-blue-100 text-blue-800';
      case 'Purged': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <span className={`px-2 py-1 text-xs rounded ${getStatusClasses()}`}>
      {status}
    </span>
  );
};

/**
 * KegTableRow - Single row in the kegs table
 * Extracted for readability and potential memoization
 */
const KegTableRow = ({ keg, settings, onViewKeg, onOpenQRModal, onEditKeg, onDeleteKeg }) => (
  <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 transition dark:text-gray-100">
    <td className="px-6 py-4">
      <div className="font-medium dark:text-gray-100">#{keg.kegNumber} - {keg.name}</div>
      <div className="text-sm text-gray-500 dark:text-gray-400">
        {keg.kegDetails.volume} {settings.volumeUnit}
      </div>
    </td>
    <td className="px-6 py-4">
      {keg.isEmpty ? (
        <span className="text-gray-400 italic">Empty</span>
      ) : (
        <div>
          <div className="font-medium dark:text-gray-100">{keg.currentBeer.name}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{keg.currentBeer.style}</div>
        </div>
      )}
    </td>
    <td className="px-6 py-4">
      {keg.tapStatus === "Empty" ? (
        <StatusBadge status={keg.cleaningStatus} type="clean" />
      ) : (
        <StatusBadge status={keg.tapStatus} type="tap" />
      )}
    </td>
    <td className="px-6 py-4">
      <div className="text-sm">
        <div className="font-bold text-gray-900 dark:text-gray-100">{keg.location}</div>
        {keg.sublocation && (
          <div className="text-gray-500 dark:text-gray-400 font-normal">{keg.sublocation}</div>
        )}
      </div>
    </td>
    <td className="px-6 py-4">
      <div className="flex gap-2">
        <button 
          onClick={() => onViewKeg(keg.id)} 
          className="text-amber-600 hover:text-amber-700 font-medium transition"
        >
          View
        </button>
        <button 
          onClick={() => onOpenQRModal(keg)} 
          className="text-purple-600 hover:text-purple-700 font-medium transition"
        >
          QR
        </button>
        <button 
          onClick={() => onEditKeg(keg)} 
          className="text-blue-600 hover:text-blue-700 font-medium transition"
        >
          Edit
        </button>
        <button 
          onClick={() => onDeleteKeg(keg.id)} 
          className="text-red-600 hover:text-red-700 font-medium transition"
        >
          Delete
        </button>
      </div>
    </td>
  </tr>
);

/**
 * DashboardKegsTab - Main kegs tab component
 * Renders a sortable/filterable table of all kegs
 */
export const DashboardKegsTab = ({ 
  filteredKegs, 
  settings, 
  onViewKeg, 
  onOpenQRModal, 
  onEditKeg, 
  onDeleteKeg 
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Keg</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Beer</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Location</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y dark:divide-gray-700">
          {filteredKegs.map(keg => (
            <KegTableRow
              key={keg.id}
              keg={keg}
              settings={settings}
              onViewKeg={onViewKeg}
              onOpenQRModal={onOpenQRModal}
              onEditKeg={onEditKeg}
              onDeleteKeg={onDeleteKeg}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DashboardKegsTab;
